import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessAlarmsRoundedIcon from "@mui/icons-material/AccessAlarmsRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { useDialog } from "../../../../components/DialogContext";
import { NoDataOptionField } from "../../../../static/staticdata";
import {
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../../styles/styles";
import {
  createTask,
  createTaskTemplate,
  getAllTask,
  getAllTaskTemplate,
  getAllTaskType,
  updateTaskOrType,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { getCases } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import noOption from "../../../../assets/no-option-found.webp";
import EmptyList from "../../../../assets/empty-todos.webp";
import UserApiField from "../../../../components/LawyerCompo/Settings/FieldsUi/UserApiField";
import {
  MatterApiField,
  TaskApiField,
} from "../../../../components/LawyerCompo/Settings/FieldsUi/InputFields";
import DrawerTemplate from "../../../../components/LawyerCompo/Cases/DrawerTemplate";
import CreateTaskTypeForm from "./CreateTaskTypeForm";

const CreateTaskForm = ({ isEditMode, editTaskId, formData, templateForm }) => {
  const { showDialog } = useDialog();
  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAssignTo, setSelectedAssignTo] = useState(null);

  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const {
    allTask,
    allTaskType,
    loading: taskLoading,
  } = useSelector((state) => state.task);
  const { allCase, loading: caseLoading } = useSelector((state) => state.case);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      task_name: "",
      priority: "normal",
      comment: "",
      client: null,
      assign_to: null,
      private_task: false,
      task_type: "",
      status: "Open",
      estimate_time: null,
      case: null,
      closed_on: "",
      task_alarm: [],
    },
  });

  useEffect(() => {
    dispatch(getCases({ url: `${backend_api}case/register/`, token }));
    dispatch(getAllTaskType({ url: `${backend_api}task/register/`, token }));
    dispatch(
      getAllTask({
        url: `${backend_api}task/task-register/`,
        token,
        teamMemberLawyerId: teamMemberLawyerId,
      })
    );
  }, [dispatch, token]);

  useEffect(() => {
    if (!isEditMode || !formData) return;

    const data = formData?.row?.full_data || formData;

    // Basic fields
    setValue("task_name", data?.task_name || "");
    setValue("priority", data?.priority?.toLowerCase() || "normal");
    setValue("comment", data?.comment || data?.description || "");
    setValue("private_task", Boolean(data?.private_task));

    // Client handling
    if (data?.client?.id) {
      setValue("client", data.client.id);
    } else if (data?.client_id) {
      setValue("client", data.client_id);
    }

    // Task type handling
    const taskType = allTaskType?.find(
      (type) =>
        type?.type_name === data?.task_type?.type_name ||
        type?.id === data?.task_type?.id
    );
    setValue("task_type", taskType?.id || null);

    // Status mapping - use the status from full_data directly since it's already mapped
    setValue("status", data?.status || "Open");

    // Estimate time handling
    if (data?.estimate_time && data.estimate_time !== "-") {
      setValue("estimate_time", dayjs(data.estimate_time));
    }

    // Due date handling - use closed_on from full_data
    if (data?.closed_on) {
      setValue("closed_on", dayjs(data.closed_on).format("YYYY-MM-DD"));
    } else if (formData?.due_date) {
      setValue("closed_on", dayjs(formData.due_date).format("YYYY-MM-DD"));
    }

    // Case handling
    if (data?.case && data?.case !== "-") {
      const caseId = typeof data.case === "object" ? data.case.id : data.case;
      setValue("case", caseId);
    }

    // Assign to handling
    if (data?.assign_to) {
      setSelectedAssignTo(data.assign_to);
      setValue("assign_to", data.assign_to.id);
    }

    // Task alarm handling
    if (Array.isArray(data?.task_alarm) && data.task_alarm.length > 0) {
      setValue(
        "task_alarm",
        data.task_alarm.map((alarm) => ({
          ...alarm,
          alram_at: alarm.alram_at ? dayjs(alarm.alram_at) : dayjs(),
          via: alarm.via || "email",
        }))
      );
    }

    console.log("Form data being processed:", data);
  }, [isEditMode, formData, allTaskType, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "task_alarm",
    keyName: "id",
  });

  const privateTaskValue = watch("private_task");

  // Helper function to clean form data
  const cleanFormData = (data) => {
    const cleanedData = {};

    // Define fields that should be included even if they're false/0
    const booleanFields = ["private_task"];

    Object.keys(data).forEach((key) => {
      const value = data[key];

      // Include boolean fields even if false
      if (booleanFields.includes(key)) {
        cleanedData[key] = Boolean(value);
        return;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length > 0) {
          // Clean each object in the array
          const cleanedArray = value
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                const cleanedItem = {};
                Object.keys(item).forEach((itemKey) => {
                  const itemValue = item[itemKey];
                  if (
                    itemValue !== null &&
                    itemValue !== undefined &&
                    itemValue !== ""
                  ) {
                    // Handle dayjs objects
                    if (dayjs.isDayjs(itemValue)) {
                      cleanedItem[itemKey] = itemValue.toISOString();
                    } else {
                      cleanedItem[itemKey] = itemValue;
                    }
                  }
                });
                return Object.keys(cleanedItem).length > 0 ? cleanedItem : null;
              }
              return item;
            })
            .filter((item) => item !== null);

          if (cleanedArray.length > 0) {
            cleanedData[key] = cleanedArray;
          }
        }
        return;
      }

      // Handle dayjs objects
      if (dayjs.isDayjs(value)) {
        cleanedData[key] = value.toISOString();
        return;
      }

      // Handle other values - exclude null, undefined, empty strings
      if (value !== null && value !== undefined && value !== "") {
        // For numbers, include 0
        if (typeof value === "number" || !isNaN(value)) {
          cleanedData[key] = value;
        } else {
          cleanedData[key] = value;
        }
      }
    });

    return cleanedData;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Clean the form data before sending
      const cleanedData = cleanFormData(data);

      console.log("Original form data:", data);
      console.log("Cleaned form data:", cleanedData);

      if (templateForm) {
        if (isEditMode) {
          await dispatch(
            updateTaskOrType({
              url: `${backend_api}task/task-template/${editTaskId}/`,
              body: cleanedData,
              token,
            })
          ).unwrap();
          toast.success("Task template updated successfully");
        } else {
          await dispatch(
            createTaskTemplate({
              url: `${backend_api}task/task-template/`,
              body: cleanedData,
              token,
            })
          ).unwrap();
          toast.success("Task template created successfully!");
        }
      } else if (isEditMode) {
        await dispatch(
          updateTaskOrType({
            url: `${backend_api}task/task-register/${editTaskId}/`,
            body: cleanedData,
            token,
          })
        ).unwrap();
        toast.success("Task updated successfully");
      } else {
        await dispatch(
          createTask({
            url: `${backend_api}task/task-register/`,
            body: cleanedData,
            token,
          })
        ).unwrap();
        toast.success("Task created successfully!");
      }

      if (templateForm) {
        dispatch(
          getAllTaskTemplate({
            url: `${backend_api}task/task-template/`,
            token,
            teamMemberLawyerId: teamMemberLawyerId,
          })
        );
      } else {
        dispatch(
          getAllTask({
            url: `${backend_api}task/task-register/`,
            token,
            teamMemberLawyerId: teamMemberLawyerId,
          })
        );
      }
      closeDialog();
      reset();
    } catch (error) {
      toast.error(
        error?.message ||
          (isEditMode
            ? templateForm
              ? "Failed to update task template"
              : "Failed to update task"
            : templateForm
            ? "Failed to create task template"
            : "Failed to create task")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAlarm = () => {
    append({
      via: "email",
      alram_at: dayjs(),
    });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            p: "1rem",
          }}
        >
          {/* Task Name and Priority */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="task_name"
                control={control}
                rules={{ required: "Task name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label={
                      <span>
                        Task Name <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors.task_name)}
                    helperText={errors.task_name?.message}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 0.5 }}>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select
                      {...field}
                      labelId="priority-label"
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Box>

          {/* Assignment and Private Task */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            {!templateForm && (
              <Box sx={{ flex: 1 }}>
                <UserApiField
                  name="assign_to"
                  label="Assign To"
                  control={control}
                  errors={errors}
                  isRequired={false}
                  apiUrl={`${backend_api}team/create-employee/`}
                  showSelfOption={true}
                  selfOptionLabel="Me (Self)"
                  defaultUser={selectedAssignTo}
                />
              </Box>
            )}

            <Box sx={{ flex: 0.5 }}>
              <Box
                id="access_privat_task_input"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${
                    privateTaskValue ? "#00B3B0" : "#c5c5c7"
                  }`,
                  borderRadius: "0.2rem",
                  px: "0.5rem",
                  height: "40px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "0.8rem", fontWeight: "500" }}
                >
                  {privateTaskValue ? "Private task" : "Visible Task"}
                </Typography>
                <Controller
                  name="private_task"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={(e) => onChange(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#00B3B0",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#00B3B0",
                          },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Task Type */}
          <Box>
          <TaskApiField
            name="task_type"
            label="Task Type"
            control={control}
            errors={errors}
            token={token}
            apiUrl={`${backend_api}task/register/`}
            placeholder="Select task type"
            TaskTypeFormComponent={CreateTaskTypeForm}
            createTaskTypeDialogTitle="Add Task Type"
            editData={allTaskType?.find((t) => t.id === watch("task_type"))}
            renderNoOptions={
              <NoDataOptionField
                imgSrc={EmptyList}
                text={"Don't have any Task Type"}
                btnText={"Task type"}
                handleClickNoData={() =>
                  showDialog(<CreateTaskTypeForm />, {
                    dialogTitle: "Add Task Type",
                  })
                }
                />
              }
            />
          </Box>

          {/* Task Status and Estimate Time */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="task_status-label">Task Status</InputLabel>
                  <Select
                    {...field}
                    labelId="task_status-label"
                    label="Task Status"
                  >
                    <MenuItem value="Open">Pending</MenuItem>
                    <MenuItem value="Transfered">In Progress</MenuItem>
                    <MenuItem value="Reviewed">In Review</MenuItem>
                    <MenuItem value="Completed">Complete</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {!templateForm && (
              <Controller
                name="estimate_time"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                    label="Estimate Time"
                    minDate={dayjs()}
                    format="MM/DD/YYYY hh:mm a"
                  />
                )}
              />
            )}
          </Box>

          {/* Case Selection */}
          {!templateForm && (
            <Box>
              <Controller
                name="case"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <MatterApiField
                    name="case"
                    value={value}
                    onChange={onChange}
                    token={token}
                    errors={errors}
                    apiUrl={`${backend_api}case/register/`}
                    generateUniqueMatterName={(id, name) => `000${id}-${name}`}
                    AddMatterFunc={() =>
                      showDialog(<DrawerTemplate />, {
                        dialogTitle: "Select Template",
                      })
                    }
                  />
                )}
              />
            </Box>
          )}

          {/* Due Date */}
          {!templateForm && (
            <Box>
              <Controller
                name="closed_on"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      const formattedValue = newValue
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : null;
                      field.onChange(formattedValue);
                    }}
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: {
                        label: "Due Date",
                        size: "small",
                        variant: "outlined",
                      },
                      popper: {
                        sx: {
                          "& .MuiPickersPopper-root": {
                            maxWidth: "100%",
                            maxHeight: "100%",
                            overflow: "hidden",
                          },
                          "& .MuiPaper-root": {
                            width: "fit-content",
                            maxHeight: "15rem",
                            overflowY: "auto",
                            ...pageScrollStyle,
                          },
                        },
                      },
                    }}
                    minDate={dayjs()}
                    format="YYYY-MM-DD"
                  />
                )}
              />
            </Box>
          )}

          {/* Description */}
          <Box>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Description"
                />
              )}
            />
          </Box>

          {!templateForm && (
            <>
              <Divider orientation="horizontal" flexItem />

              {/* Reminders Section */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="600">
                    Reminders
                  </Typography>
                  {fields.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      onClick={handleAddAlarm}
                      sx={{ ml: "auto" }}
                    >
                      Add Reminder
                    </Button>
                  )}
                </Box>

                {fields.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    {fields.map((field, index) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          p: "1rem",
                          border: "1px solid #e0e0e0",
                          borderRadius: "0.5rem",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Controller
                          name={`task_alarm.${index}.via`}
                          control={control}
                          defaultValue={field.via}
                          render={({ field }) => (
                            <Select {...field} size="small" sx={{ flex: 1 }}>
                              <MenuItem value="email">Me (via Email)</MenuItem>
                              <MenuItem value="notification">
                                Me (via Notification)
                              </MenuItem>
                            </Select>
                          )}
                        />

                        <Controller
                          name={`task_alarm.${index}.alram_at`}
                          control={control}
                          defaultValue={field.alram_at}
                          render={({ field }) => (
                            <DateTimePicker
                              {...field}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: { width: "16rem" },
                                },
                              }}
                              minDate={dayjs()}
                              format="MM/DD/YYYY hh:mm a"
                            />
                          )}
                        />

                        <IconButton
                          onClick={() => remove(index)}
                          aria-label="Remove alarm"
                          size="small"
                          sx={{
                            color: "error.main",
                            "&:hover": {
                              backgroundColor: "error.light",
                              color: "white",
                            },
                          }}
                        >
                          <CloseRoundedIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      my: 2,
                      p: "2rem",
                      border: "2px dashed #e0e0e0",
                      borderRadius: "0.5rem",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <AccessAlarmsRoundedIcon
                      sx={{
                        fontSize: "4rem",
                        color: "primary.main",
                        opacity: "0.5",
                      }}
                    />
                    <Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        No reminders set for this task
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Add reminders to get notified about this task via email
                        or notification
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleAddAlarm}
                      >
                        Add First Reminder
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Form Footer/Actions */}
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button variant="outlined" size="small" onClick={closeDialog}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
          >
            {isEditMode
              ? templateForm
                ? "Update Task Template"
                : "Update Task"
              : templateForm
              ? "Create Task Template"
              : "Create Task"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateTaskForm;
